import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { Klausur } from "@/lib/orgaAdmin/orgaAdminKlausur.service";
import { BookOpen, Upload, SquarePen, List } from "lucide-react";

interface KlausurListCardProps {
	/** List of klausuren to display */
	klausurs: Klausur[];
	/** Loading state for klausuren */
	isLoading: boolean;
	/** Optional title for the card header */
	title?: string;
	/** Optional click handler to make items clickable */
	onKlausurClick?: (klausur: Klausur) => void;
	/** Optional upload handler for per-klausur uploads */
	onUploadClick?: (klausur: Klausur) => void;
	/** Optional handler to view student klausuren for a specific klausur */
	onViewStudentKlausurenClick?: (klausur: Klausur) => void;
	/** Optional handler to create a new klausur */
	onCreateKlausurClick?: () => void;
	/** Optional disabled state for create card */
	isCreateDisabled?: boolean;
}

/**
 * klausurListCard - A component that displays a list of klausuren
 *
 * This component displays a list of klausuren, each with a title, index, and number of student klausuren.
 *
 * @param {klausurs} - The list of klausuren to display
 * @param {isLoading} - The loading state for klausuren
 * @param {title} - The title of the card header
 * @param {onKlausurClick} - A callback function that is called when a klausur is clicked
 * @param {onUploadClick} - A callback function that is called when upload button is clicked
 */
export default function KlausurListCard({
	klausurs,
	isLoading,
	title = "Verfügbare Klausuren",
	onKlausurClick,
	onUploadClick,
	onViewStudentKlausurenClick,
}: KlausurListCardProps) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>{title}</CardTitle>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<div className="flex items-center justify-center py-8">
						<div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
						<span className="ml-2">Klausuren werden geladen...</span>
					</div>
				) : klausurs.length === 0 ? (
					<div className="text-center py-8 text-muted-foreground">
						Keine Klausuren in dieser Organisation gefunden.
					</div>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{klausurs.map((klausur) => (
							<Card
								key={klausur.id}
								className="border-2 transition-all hover:shadow-md"
							>
								<CardHeader className="pb-3">
									<CardTitle className="text-base flex items-center gap-2">
										<BookOpen className="h-5 w-5 text-primary" />
										{klausur.title}
									</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="space-y-2 text-sm">
										<div className="flex justify-between">
											<span className="text-muted-foreground">Index:</span>
											<span className="font-medium">{klausur.index}</span>
										</div>
										<div className="flex justify-between">
											<span className="text-muted-foreground">
												Studentenklausuren:
											</span>
											<span className="font-medium">
												{klausur.studentKlausuren?.length || 0}
											</span>
										</div>
									</div>

									<div className="flex flex-col gap-2">
										{onUploadClick && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													onUploadClick(klausur);
												}}
												className="w-full gap-2"
												size="sm"
											>
												<Upload className="h-4 w-4" />
												Studentenklausuren hochladen
											</Button>
										)}
										{onViewStudentKlausurenClick && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													onViewStudentKlausurenClick(klausur);
												}}
												variant="outline"
												className="w-full gap-2"
												size="sm"
												disabled={!klausur.studentKlausuren?.length}
											>
												<List className="h-4 w-4" />
												Studentenklausuren anzeigen
											</Button>
										)}
										{onKlausurClick && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													onKlausurClick(klausur);
												}}
												variant="outline"
												className="w-full gap-2"
												size="sm"
											>
												<SquarePen className="h-4 w-4" />
												Klausur Bearbeiten
											</Button>
										)}
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
